import React from 'react';

import type { DateRange } from '@/nova/pages/affiliates/reports-helpers';
import type { Metrics } from '@/nova/pages/home/stats-shape';

import { assetLinks } from './link-attribution';
import type { TrackingLink, TrackingLinkKind } from './tracking-links-api';
import { useBrandLinks } from './useBrandLinks';
import { useCampaignStats } from './useCampaignStats';

/**
 * The data layer of a marketing asset's stats sheet (`components/AssetStats/AssetStatsSheet`): the asset's tracking
 * links (`assetLinks` over the brand's links: an asset's own, one per version; a landing page's, every link landing on
 * it), then their `campaign` values summed over the selected range from the Owners Hub Stats `daily_affiliate_activity`
 * data set (`useCampaignStats`).
 *
 * The brand's links are read again as the sheet opens (the cached answer is up to a minute old, and the owner may have
 * just got a tracking link in the composer underneath), so a link minted a moment ago already counts.
 */
export type AssetStatsStatus = 'loading' | 'no-brand' | 'no-campaigns' | 'error' | 'ok';

export interface AssetStatsData {
  status: AssetStatsStatus;
  /** The asset's links with a campaign (empty until they are read). */
  links: TrackingLink[];
  /** Where the links read is: `ready` once the fresh read landed (then `links` is the answer, maybe none). */
  linksStatus: 'loading' | 'ready' | 'error';
  /** One metric object per matched link campaign; the sheet sums the KPI fields across them. */
  metricsList: Metrics[];
  /** The same, keyed by campaign (a per-version / per-link breakdown). */
  byCampaign: ReadonlyMap<string, Metrics>;
  error?: string;
  reload: () => void;
}

export interface UseAssetStatsInput {
  kind: TrackingLinkKind;
  /** The asset's spark creatives id (a landing page: its spark id). */
  creativeId: string;
  range: DateRange;
}

const NO_LINKS: TrackingLink[] = [];

export function useAssetStats({ kind, creativeId, range }: UseAssetStatsInput): AssetStatsData {
  const brand = useBrandLinks();
  const { reload: reloadLinks } = brand;

  // Syncs with spark-creatives: a fresh read of the brand's links as the sheet opens (see the module note).
  React.useEffect(() => {
    reloadLinks();
  }, [reloadLinks]);

  const settled = !brand.loading;
  const links = React.useMemo(
    () => (settled ? assetLinks(brand.links, kind, creativeId) : NO_LINKS),
    [settled, brand.links, kind, creativeId],
  );
  const campaigns = React.useMemo(() => [...new Set(links.map((l) => l.campaign))], [links]);
  const stats = useCampaignStats({ campaigns, range });
  const { reload: reloadStats } = stats;
  const reload = React.useCallback((): void => {
    reloadLinks();
    reloadStats();
  }, [reloadLinks, reloadStats]);

  const linksStatus: AssetStatsData['linksStatus'] = brand.loading
    ? 'loading'
    : !brand.tenantId || (brand.error && brand.links.length === 0)
      ? 'error'
      : 'ready';
  const base = { links, linksStatus, metricsList: [], byCampaign: stats.byCampaign, reload };
  if (!brand.tenantId) return { ...base, status: brand.loading ? 'loading' : 'no-brand' };
  if (brand.loading) return { ...base, status: 'loading' };
  if (brand.error && brand.links.length === 0) return { ...base, status: 'error', error: brand.error };
  if (campaigns.length === 0) return { ...base, status: 'no-campaigns' };
  if (stats.status === 'no-brand') return { ...base, status: 'no-brand' };
  if (stats.status === 'error') return { ...base, status: 'error', error: stats.error };
  // `none` with campaigns in hand: the stats read has not caught up with the links yet (one render).
  if (stats.status === 'loading' || stats.status === 'none') return { ...base, status: 'loading' };
  return { ...base, status: 'ok', metricsList: stats.metricsList };
}

export default useAssetStats;
